import * as Yup from 'yup';

// --- Model (Interface) ---
export interface IMaintenanceModel {
  id: string;
  date: Date | null;
  amount: number | null;
  currency: string;
  tax: number | null;
  remainder: number | null;
  expenseName: string;
  branchId: string;
  documentNo: string;
  transactor: string;
  carId: string;
  customerId: string;
  dateSent: Date | null;
  timeSent: string; // LocalTime için string kullanılabilir veya özel bir formatlama yapılabilir
  plannedDate: Date | null;
  plannedTime: string; // LocalTime için
  previousDescription: string;
  outKM: number | null;
  outFuelLevel: string;
  periodicService: boolean;
  damageAccident: boolean;
  mechanicalFailure: boolean;
  maintenance: boolean;
  payer: string;
  status: string;
  accidentResponsible: string;
  nextDescription: string;
  inKM: number | null;
  inFuelLevel: string;
  endDate: Date | null;
  endTime: string; // LocalTime için
  warningKm: string;
  paymentDate: Date | null;
  payment: number | null;
  paymentType: string;
  cashRegisterCode: string;
  iban: string;
  description: string;
  readyDescription: string;
  agreementNo: string;
  reflectionAmount: number | null;
  bodyWork: number;
  paintWork: number;
  trimWork: number;
  electricWork: number;
  mechanicWork: number;
  chassisWork: number;
}

// --- Initial Values ---
export const initialMaintenance: IMaintenanceModel = {
  id: '',
  date: null,
  amount: null,
  currency: '',
  tax: null,
  remainder: null,
  expenseName: '',
  branchId: '',
  documentNo: '',
  transactor: '',
  carId: '',
  customerId: '',
  dateSent: null,
  timeSent: '',
  plannedDate: null,
  plannedTime: '',
  previousDescription: '',
  outKM: null,
  outFuelLevel: '',
  periodicService: false,
  damageAccident: false,
  mechanicalFailure: false,
  maintenance: false,
  payer: '',
  status: '',
  accidentResponsible: '',
  nextDescription: '',
  inKM: null,
  inFuelLevel: '',
  endDate: null,
  endTime: '',
  warningKm: '',
  paymentDate: null,
  payment: null,
  paymentType: '',
  cashRegisterCode: '',
  iban: '',
  description: '',
  readyDescription: '',
  agreementNo: '',
  reflectionAmount: null,
  bodyWork: 0,
  paintWork: 0,
  trimWork: 0,
  electricWork: 0,
  mechanicWork: 0,
  chassisWork: 0,
};

// --- Validation Schema ---
export const validationMaintenance = Yup.object().shape({
  date: Yup.date().nullable().typeError('Geçerli bir tarih girin'),
  amount: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .positive('Pozitif bir değer girin'),
  currency: Yup.string(),
  tax: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .positive('Pozitif bir değer girin'),
  remainder: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .positive('Pozitif bir değer girin'),
  expenseName: Yup.string(),
  branchId: Yup.string(),
  documentNo: Yup.string(),
  transactor: Yup.string(),
  carId: Yup.string().required('Araç ID zorunludur'),
  customerId: Yup.string(),
  dateSent: Yup.date().nullable().typeError('Geçerli bir tarih girin'),
  timeSent: Yup.string(),
  plannedDate: Yup.date().nullable().typeError('Geçerli bir tarih girin'),
  plannedTime: Yup.string(),
  previousDescription: Yup.string(),
  outKM: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .integer('Tam sayı girin')
    .min(0, 'Kilometre sıfır veya pozitif olmalı'),
  outFuelLevel: Yup.string(),
  periodicService: Yup.boolean(),
  damageAccident: Yup.boolean(),
  mechanicalFailure: Yup.boolean(),
  maintenance: Yup.boolean(),
  payer: Yup.string(),
  status: Yup.string(),
  accidentResponsible: Yup.string(),
  nextDescription: Yup.string(),
  inKM: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .integer('Tam sayı girin')
    .min(0, 'Kilometre sıfır veya pozitif olmalı'),
  inFuelLevel: Yup.string(),
  endDate: Yup.date().nullable().typeError('Geçerli bir tarih girin'),
  endTime: Yup.string(),
  warningKm: Yup.string(),
  paymentDate: Yup.date().nullable().typeError('Geçerli bir tarih girin'),
  payment: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .positive('Pozitif bir değer girin'),
  paymentType: Yup.string(),
  cashRegisterCode: Yup.string(),
  iban: Yup.string(),
  description: Yup.string(),
  readyDescription: Yup.string(),
  agreementNo: Yup.string(),
  reflectionAmount: Yup.number()
    .nullable()
    .typeError('Sayısal bir değer girin')
    .positive('Pozitif bir değer girin'),
});


// Hasar / Kaza, Mekanik Arıza ve Bakım alanları için seçenekler
export const CURRENCY_OPTIONS = [
{ value: 'TRY', label: 'Türk Lirası' },
{ value: 'USD', label: 'Amerikan Doları' },
{ value: 'EUR', label: 'Euro' },
] as const;

export const STATUS_OPTIONS = [
{ value: 'Devam Ediyor', label: 'Devam Ediyor' },
{ value: 'Tamamlandı', label: 'Tamamlandı' },
{ value: 'Bitmek Üzere', label: 'Bitmek Üzere' },
] as const;


export const PAYER_OPTIONS = [
{ value: 'Biz', label: 'Biz' },
{ value: 'Karşı Taraf', label: 'Karşı Taraf' },
{ value: 'Sigorta', label: 'Sigorta' },
] as const;


export const PAYMENT_TYPE_OPTIONS = [
{ value: 'Nakit', label: 'Nakit' },
{ value: 'Kredi Kartı', label: 'Kredi Kartı' },
{ value: 'Banka Kartı', label: 'Banka Kartı' },
] as const;


export const CASH_REGISTER_CODE_OPTIONS  = [
{ value: 'Temel', label: 'Temel' },
{ value: 'Özel', label: 'Özel' },
{ value: 'Premium', label: 'Premium' },
] as const;

export const IBAN_OPTIONS  = [
{ value: 'Kurumsal', label: 'Kurumsal' },
{ value: 'Özel', label: 'Özel' },
{ value: 'Premium', label: 'Premium' },
] as const;


export const READYDESRIPTION_OPTIONS  = [
{ value: 'Acil Ödenmeli', label: 'Acil Ödenmeli' },
{ value: 'Gecikmiş Ödemiş', label: 'Gecikmiş Ödemiş' },
{ value: 'Zamanında Ödeme', label: 'Zamanında Ödeme' },
] as const;


export const LIST_OPTIONS = [
{ value: 'Tarih', label: 'Tarih' },
{ value: 'İşlem', label: 'İşlem' },
{ value: 'Diğer', label: 'Diğer' },
] as const;

export const EXPENSE_OPTIONS = [
{ value: 'Tüm Giderler', label: 'Tüm Giderler' },
{ value: 'Özel Giderler', label: 'Özel Giderler' },
{ value: 'Diğer', label: 'Diğer' },
] as const;

export const EXTRA_OPTIONS = [
{ value: 'Bitti', label: 'Bitti' },
{ value: 'Devam Ediyor', label: 'Devam Ediyor' },
{ value: 'Diğer', label: 'Diğer' },
] as const;

export const EXTRA_OPTIONS2 = [
{ value: 'Kira Kaydını Gizle', label: 'Kira Kaydını Gizle' },
{ value: 'Kira Kaydını Göster', label: 'Kira Kaydını Göster' },
{ value: 'Diğer', label: 'Diğer' },
] as const;